import {
  type ProfileFormData,
  type ProfileUpdateData,
  profileFormSchema,
} from "@stonecut/schemas";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { ZodIssue } from "zod";
import Loader from "@/components/loader";
import { PageHeader } from "@/components/page-header";
import {
  FieldErrorMessage,
  FormErrorAlert,
} from "@/components/stone/form-errors";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getErrorMessage } from "@/lib/form-errors";
import { useUser } from "@/lib/user-context";
import { trpc } from "@/utils/trpc";

type UserProfile = {
  fullName?: string | null;
  company?: string | null;
  jobTitle?: string | null;
  phoneNumber?: string | null;
};

function buildInitialProfileFormData(userProfileData: UserProfile) {
  return {
    fullName: userProfileData.fullName || "",
    company: userProfileData.company || "",
    jobTitle: userProfileData.jobTitle || "",
    phoneNumber: userProfileData.phoneNumber || "",
  } satisfies ProfileFormData;
}

function buildProfileUpdates(
  formData: ProfileFormData,
  savedData: ProfileFormData
): ProfileUpdateData {
  const updates: ProfileUpdateData = {};

  if (formData.fullName.trim() !== savedData.fullName) {
    updates.fullName = formData.fullName.trim();
  }

  if (formData.company.trim() !== savedData.company) {
    updates.company = formData.company.trim();
  }

  if (formData.jobTitle.trim() !== savedData.jobTitle) {
    updates.jobTitle = formData.jobTitle.trim();
  }

  const phoneNumber = formData.phoneNumber ?? "";
  if (phoneNumber.trim() !== savedData.phoneNumber) {
    updates.phoneNumber = phoneNumber.trim() || undefined;
  }

  return updates;
}

function EditProfileForm({
  profileId,
  userProfileData,
  showHeader = true,
  showCancel = true,
}: {
  profileId: string;
  userProfileData: UserProfile;
  showHeader?: boolean;
  showCancel?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<ProfileFormData>(() =>
    buildInitialProfileFormData(userProfileData)
  );
  const [savedData, setSavedData] = useState<ProfileFormData>(() =>
    buildInitialProfileFormData(userProfileData)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    const nextData = buildInitialProfileFormData(userProfileData);
    setFormData(nextData);
    setSavedData(nextData);
  }, [userProfileData]);

  const updateProfileMutation = useMutation({
    ...trpc.updateUserProfile.mutationOptions(),
    onSuccess: async (_data, variables) => {
      setSavedData((previous) => ({
        ...previous,
        ...Object.fromEntries(
          Object.entries(variables).map(([key, value]) => [key, value ?? ""])
        ),
      }));
      setSaveMessage("Saved");
      await queryClient.invalidateQueries({
        queryKey: trpc.getUserProfile.queryOptions().queryKey,
      });
    },
    onError: (error) => {
      const message = getErrorMessage(error, "Failed to update profile");
      setErrors({ general: message });
      setSaveMessage(null);
      toast.error(message);
    },
  });

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
    setSaveMessage(null);
    if (errors[field] || errors.general) {
      setErrors((previous) => ({
        ...previous,
        [field]: "",
        general: "",
      }));
    }
  };

  const validateForm = () => {
    const result = profileFormSchema.safeParse(formData);

    if (!result.success) {
      const nextErrors: Record<string, string> = {};
      result.error.issues.forEach((issue: ZodIssue) => {
        const field = typeof issue.path[0] === "string" ? issue.path[0] : "";
        if (field) {
          nextErrors[field] = issue.message;
        }
      });
      setErrors(nextErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleBlurSave = () => {
    if (!validateForm() || updateProfileMutation.isPending) {
      return;
    }

    const updates = buildProfileUpdates(formData, savedData);
    if (Object.keys(updates).length === 0) {
      return;
    }

    setSaveMessage("Saving...");
    updateProfileMutation.mutate(updates);
  };

  return (
    <div key={profileId} className="space-y-6">
      {showHeader ? (
        <PageHeader
          title="Edit Profile"
          description="Update your personal information"
        />
      ) : null}

      <Card>
        {!showHeader ? (
          <CardHeader className="pb-2">
            <CardTitle>Edit Profile</CardTitle>
          </CardHeader>
        ) : null}
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name</Label>
            <Input
              id="fullName"
              type="text"
              value={formData.fullName}
              aria-invalid={Boolean(errors.fullName)}
              onChange={(event) =>
                handleInputChange("fullName", event.target.value)
              }
              onBlur={handleBlurSave}
              placeholder="Enter your full name"
            />
            <FieldErrorMessage message={errors.fullName} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company">Company</Label>
            <Input
              id="company"
              type="text"
              value={formData.company}
              aria-invalid={Boolean(errors.company)}
              onChange={(event) =>
                handleInputChange("company", event.target.value)
              }
              onBlur={handleBlurSave}
              placeholder="Enter your company name"
            />
            <FieldErrorMessage message={errors.company} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="jobTitle">Job Title</Label>
            <Input
              id="jobTitle"
              type="text"
              value={formData.jobTitle}
              aria-invalid={Boolean(errors.jobTitle)}
              onChange={(event) =>
                handleInputChange("jobTitle", event.target.value)
              }
              onBlur={handleBlurSave}
              placeholder="Electrician, Crane Operator, etc."
            />
            <FieldErrorMessage message={errors.jobTitle} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phoneNumber">Phone Number (Optional)</Label>
            <Input
              id="phoneNumber"
              type="tel"
              value={formData.phoneNumber}
              onChange={(event) =>
                handleInputChange("phoneNumber", event.target.value)
              }
              onBlur={handleBlurSave}
              placeholder="Enter your phone number"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-2">
        {saveMessage ? (
          <p className="text-sm text-muted-foreground">{saveMessage}</p>
        ) : null}
        {showCancel ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/account")}
          >
            Cancel
          </Button>
        ) : null}
      </div>

      <FormErrorAlert message={errors.general} />
    </div>
  );
}

export default function EditProfile() {
  const { session } = useUser();

  const {
    data: userProfileData,
    isPending: userProfilePending,
    isError: userProfileError,
    error: userProfileErrorValue,
  } = useQuery({
    ...trpc.getUserProfile.queryOptions(),
    enabled: !!session,
  });

  if (userProfilePending) {
    return <Loader className="min-h-screen" />;
  }

  if (!session) {
    return null;
  }

  if (userProfileData === null) {
    return <Navigate to="/account" replace />;
  }

  if (userProfileError) {
    return (
      <FormErrorAlert
        message={
          userProfileErrorValue instanceof Error
            ? userProfileErrorValue.message
            : "Failed to load profile"
        }
      />
    );
  }

  if (!userProfileData) {
    return null;
  }

  const profileId = (session.user as { id?: string } | undefined)?.id ?? "self";

  return (
    <EditProfileForm
      key={profileId}
      profileId={profileId}
      userProfileData={userProfileData}
    />
  );
}

export function EmbeddedEditProfileSection() {
  const { session } = useUser();

  const { data: userProfileData, isPending: userProfilePending } = useQuery({
    ...trpc.getUserProfile.queryOptions(),
    enabled: !!session,
  });

  if (userProfilePending) {
    return <Loader layout="inline" size="sm" label="Loading profile" />;
  }

  if (!session || !userProfileData) {
    return null;
  }

  const profileId = (session.user as { id?: string } | undefined)?.id ?? "self";

  return (
    <EditProfileForm
      key={profileId}
      profileId={profileId}
      userProfileData={userProfileData}
      showHeader={false}
      showCancel={false}
    />
  );
}
